import { useEffect, useMemo, useState } from "react";
import { SubventionsEmpty } from "@/features/subventions/components/SubventionsEmpty";
import { ChequesFeedCard } from "@/features/subventions/components/feed/ChequesFeedCard";
import { HolderSwitcher } from "@/features/subventions/components/feed/HolderSwitcher";
import { SubventionDetailDrawer } from "@/features/subventions/components/feed/SubventionDetailDrawer";
import { SubventionsFeed } from "@/features/subventions/components/feed/SubventionsFeed";
import { UniverseFilter } from "@/features/subventions/components/feed/UniverseFilter";
import { buildFeedHolders, holderContextLabel } from "@/features/subventions/components/feed/feedHolder";
import type { ChequesData } from "@/features/subventions/types/subsidy-sumup.types";
import type { EligibleSubsidy, SubsidiesResponse } from "@/features/subventions/types/subvention.types";

type SubventionsFeedViewProps = {
  data: SubsidiesResponse | undefined
  cheques: ChequesData | null
};

const FOOTNOTE = "* des exclusions peuvent s'appliquer. Les limites sont toutes cumulables, la limite la plus basse s'applique sur la période (la limite la plus contraignante).";

export const SubventionsFeedView = ({ data, cheques }: SubventionsFeedViewProps) => {
  const holders = useMemo(() => buildFeedHolders(data), [data]);
  const holderSignature = holders.map((holder) => holder.id).join("|");

  const [activeHolderIndex, setActiveHolderIndex] = useState(0);
  const [universeFilter, setUniverseFilter] = useState<Set<string>>(new Set());
  const [selectedSubsidy, setSelectedSubsidy] = useState<EligibleSubsidy | null>(null);

  // Reset view state only when the set of holders changes (e.g. a refetch), not on every render.
  useEffect(() => {
    setActiveHolderIndex(0);
    setUniverseFilter(new Set());
    setSelectedSubsidy(null);
  }, [holderSignature]);

  const hasCheques = cheques !== null && cheques.cheques.length > 0;

  if (!holders.length) {
    return (
      <div className="flex flex-col gap-6">
        {hasCheques && <ChequesFeedCard cheques={cheques} />}
        {!hasCheques && <SubventionsEmpty />}
      </div>
    );
  }

  const activeHolder = holders[Math.min(activeHolderIndex, holders.length - 1)];

  const selectHolder = (index: number) => {
    setActiveHolderIndex(index);
    setUniverseFilter(new Set());
    // Close any open drawer so it can't show holder A's subsidy while the holder id flips to B
    // (which would fetch history for the wrong pair). The close animation still plays via the
    // drawer's own `rendered` state.
    setSelectedSubsidy(null);
  };

  const toggleUniverse = (universe: string) => {
    setUniverseFilter((previous) => {
      const next = new Set(previous);
      if (next.has(universe)) next.delete(universe);
      else next.add(universe);
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-5">
      {hasCheques && <ChequesFeedCard cheques={cheques} />}

      <HolderSwitcher holders={holders} activeIndex={activeHolderIndex} onSelect={selectHolder} />

      <UniverseFilter subsidies={activeHolder.subsidies} selected={universeFilter} onToggle={toggleUniverse} />

      <div>
        <h2 className="mb-3 px-0.5 text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          Vos subventions disponibles
        </h2>
        <SubventionsFeed
          subsidies={activeHolder.subsidies}
          universeFilter={universeFilter}
          onOpenDetail={setSelectedSubsidy}
        />
      </div>

      <p className="border-t border-gray-200 pt-4 text-[11px] italic text-gray-400 dark:border-gray-700 dark:text-gray-500">
        {FOOTNOTE}
      </p>

      <SubventionDetailDrawer
        subsidy={selectedSubsidy}
        holderLabel={holderContextLabel(activeHolder)}
        holderId={activeHolder.id}
        onClose={() => setSelectedSubsidy(null)}
      />
    </div>
  );
};

SubventionsFeedView.displayName = "SubventionsFeedView";
